import React, { useEffect, useMemo, useState } from 'react';
import { Button, Tooltip } from '@attentive-platform/stem-ui';

import { parseCustomDay } from './customCards';
import { TatConsoleStyles as S } from './styles';

const CustomCardModal = ({
  open,
  mode,
  tradeName,
  resolvedName,
  ranges,
  initialValues,
  wasValues,
  initialReason,
  actorName,
  submitting,
  onCancel,
  onSave
}) => {
  const [values, setValues] = useState(initialValues || []);
  const [reason, setReason] = useState(initialReason || '');

  useEffect(() => {
    if (!open) return;
    setValues(initialValues || []);
    setReason(initialReason || '');
  }, [open, initialValues, initialReason]);

  const parsed = useMemo(() => (values || []).map(parseCustomDay), [values]);
  const fieldError = parsed.some(v => v === undefined);
  const saveDisabled = fieldError || !(reason || '').trim() || !!submitting;
  const saveTitle = submitting ? 'Saving…' : saveDisabled ? 'Every range needs a value and the reason needs text' : '';

  if (!open) return null;

  const title = mode === 'edit' ? `Edit custom card · ${tradeName}` : `Set custom card · ${tradeName}`;
  const saveLabel = mode === 'edit' ? 'Save changes' : 'Set custom card';
  const footnote = `Saving mints a new scope version with the same selections, labelled "Custom card ${
    mode === 'edit' ? 'changed' : 'set'
  } by ${actorName}". No customer email or PDF is sent. The internal benchmark stays the internal card of the account preset.`;

  return (
    <>
      <S.Overlay onClick={onCancel} />
      <S.Modal role='dialog' aria-modal='true' aria-label={title}>
        <S.ModalHead>
          <S.ModalTitle>{title}</S.ModalTitle>
          <S.ModalSub>
            A customer card for this account only, in place of {resolvedName}. It stands in at submission whenever the
            takeoff preset is the account preset.
          </S.ModalSub>
        </S.ModalHead>
        <S.ModalBody>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <S.FieldLabel>
              Business days per range <span style={{ color: '#9E9E9E', fontWeight: 400 }}>1–60, or Quote</span>
            </S.FieldLabel>
            <S.FieldsGrid $cols={ranges.length}>
              {ranges.map((r, i) => (
                <S.FieldCol key={r}>
                  <span style={{ fontSize: 11, color: '#808080', textAlign: 'center' }}>{r}</span>
                  <S.DayInput
                    $bad={parsed[i] === undefined}
                    value={values[i] ?? ''}
                    aria-label={`${r} sheets`}
                    onChange={e => {
                      const next = values.slice();
                      next[i] = e.target.value;
                      setValues(next);
                    }}
                  />
                  <S.Was>was {wasValues?.[i] ?? '—'}</S.Was>
                </S.FieldCol>
              ))}
            </S.FieldsGrid>
            {fieldError ? (
              <S.FieldError>Every range needs a whole number of business days from 1 to 60, or Quote.</S.FieldError>
            ) : null}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <S.FieldLabel>
              Reason <S.Req>(required)</S.Req>
            </S.FieldLabel>
            <S.TextArea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder='Say what the account needs that no preset covers.'
              rows={3}
            />
            <S.Hint>Say what the account needs that no preset covers.</S.Hint>
          </div>
          <S.Footnote>
            <S.FootnoteDot />
            <span style={{ fontSize: '12.5px', lineHeight: 1.5, color: '#4D4D4D' }}>{footnote}</span>
          </S.Footnote>
        </S.ModalBody>
        <S.ModalFoot>
          <Button variant='text' color='primary' size='large' onClick={onCancel}>
            Cancel
          </Button>
          <Tooltip arrow placement='top' variant='dark' title={saveTitle || undefined}>
            <span>
              <Button
                variant='contained'
                color='primary'
                size='large'
                disabled={saveDisabled}
                onClick={() => {
                  if (saveDisabled) return;
                  onSave({
                    values: parsed.map(v => (v === 'Quote' ? null : v)),
                    reason: reason.trim()
                  });
                }}>
                {saveLabel}
              </Button>
            </span>
          </Tooltip>
        </S.ModalFoot>
      </S.Modal>
    </>
  );
};

export default CustomCardModal;
